// ✅ Trekkin.app — PanelShell.jsx v3.7.3 (SAFE PROPS + PRESERVE MODE)
// -----------------------------------------------------------------------------
// ✔ Mantém TODO o comportamento do v3.7.2
// ✔ Se preserve=true → o painel NÃO desmonta ao regressar
// ✔ FIX DEFINITIVO React #31:
//     • Nunca espalha props inválidas
//     • Protege contra objetos { type, message }
//     • Logs de debug para detetar origem do problema
// ✔ NÃO altera layout, UX ou stacking
// -----------------------------------------------------------------------------

import { Suspense } from "react";
import { usePanelsStore } from "../stores/usePanelsStore";
import LoadingDotsOverlay from "./LoadingDotsOverlay.jsx";
import { PANEL_COMPONENTS, PANEL_FALLBACK } from "./panelRegistry.jsx";

export default function PanelShell({ children = null }) {
  const { currentPanel, closePanel } = usePanelsStore();

  if (!currentPanel) return null;

  const PanelComponent =
    PANEL_COMPONENTS[currentPanel.name] || PANEL_FALLBACK;
  const preserve = currentPanel?.props?.preserve === true;
  const allowMapInteraction = currentPanel.name === "mapSelectPoint";
  const isBikeManagementPanel = ["bikeService", "bikeRental"].includes(currentPanel.name);

  // -------------------------------------------------------------
  // VIEWER MODE (?share=) — nunca mostrar painéis
  // -------------------------------------------------------------
  const isViewer = (() => {
    try {
      return new URLSearchParams(window.location.search).has("share");
    } catch {
      return false;
    }
  })();

  if (isViewer) return null;

  // -------------------------------------------------------------
  // 🛡️ SAFE PROPS (FIX React error #31)
  // -------------------------------------------------------------
  const rawProps = currentPanel?.props;

  const safeProps =
    rawProps && typeof rawProps === "object" && !Array.isArray(rawProps)
      ? rawProps
      : {};

  // 🧪 DEBUG — identificar props perigosas
  if (
    safeProps &&
    typeof safeProps === "object" &&
    ("type" in safeProps || "message" in safeProps)
  ) {
    console.warn(
      "🚨 [PanelShell] Props potencialmente perigosas recebidas:",
      safeProps,
      "Painel:",
      currentPanel.name
    );
  }

  // -------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------
  return (
    <div
      className={`panel-shell-wrapper${currentPanel.name === "admin" ? " panel-shell-admin" : ""}${isBikeManagementPanel ? " panel-shell-bike-management" : ""}${allowMapInteraction ? " panel-shell-map-select" : ""}`}
      onPointerDown={(event) => {
        if (allowMapInteraction || event.target !== event.currentTarget) return;
        closePanel();
      }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        pointerEvents: allowMapInteraction ? "none" : "auto",
        zIndex: 2000,
      }}
    >
      <div
        className={currentPanel.name === "admin"
          ? "panel-shell-admin-scroll"
          : allowMapInteraction
            ? "panel-shell-map-select-card"
            : isBikeManagementPanel
              ? "panel-shell-bike-management-card"
              : undefined}
        style={{
          position: "absolute",
          top: allowMapInteraction
            ? "auto"
            : "max(10px, env(safe-area-inset-top), var(--tk-native-safe-top, 0px))",
          right: allowMapInteraction ? "12px" : "10px",
          bottom: allowMapInteraction
            ? "calc(92px + max(env(safe-area-inset-bottom, 0px), var(--tk-native-safe-bottom, 0px)))"
            : "calc(78px + max(env(safe-area-inset-bottom, 0px), var(--tk-native-safe-bottom, 0px)))",
          left: allowMapInteraction ? "12px" : "10px",
          width: "auto",
          maxWidth: "520px",
          margin: "0 auto",
          maxHeight: "none",
          overflowY: allowMapInteraction ? "visible" : "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "contain",
          padding: allowMapInteraction ? 0 : "10px",
          backdropFilter: allowMapInteraction ? "none" : "blur(14px)",
          background: allowMapInteraction ? "transparent" : "var(--tk-panel-bg, rgba(255,255,255,0.85))",
          boxShadow: allowMapInteraction ? "none" : "var(--tk-panel-shadow, 0 4px 18px rgba(0,0,0,0.18))",
          color: "var(--tk-text, #17221b)",
          borderRadius: allowMapInteraction ? 0 : "18px",
          transition: "none",
          pointerEvents: allowMapInteraction ? "none" : "auto",
        }}
      >
        <div className="tk-panel-content">
          <Suspense fallback={<LoadingDotsOverlay zIndex={2100} />}>
            {children || (
              <PanelComponent
                key={`${currentPanel.name}${preserve ? "-preserve" : ""}`}
                {...safeProps}
              />
            )}
          </Suspense>
        </div>
      </div>
    </div>
  );
}
